import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, Share, Plus, MoreVertical, X, Smartphone, Monitor, Check } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PWAInstallPrompt: React.FC = () => {
  const [showMobileInstructions, setShowMobileInstructions] = useState(false);
  const [showDesktopPrompt, setShowDesktopPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [installSuccess, setInstallSuccess] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       (window.navigator as any).standalone === true;
    setIsStandalone(standalone);

    if (standalone) return; // Don't show prompts if already installed

    // Detect device type
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent) && !(window as any).MSStream;
    const isAndroidDevice = /android/.test(userAgent);
    
    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);

    // Check if we've shown the mobile instructions before
    const hasSeenMobileInstructions = localStorage.getItem('pwa-mobile-instructions-seen');
    
    // For iOS/Android, show instructions on first visit
    if ((isIOSDevice || isAndroidDevice) && !hasSeenMobileInstructions) {
      // Small delay to let the app load first
      setTimeout(() => {
        setShowMobileInstructions(true);
      }, 2000);
    }

    // Listen for the beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      setDeferredPrompt(null);
      setInstallSuccess(true);
      setTimeout(() => setInstallSuccess(false), 3000);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleMobileInstructionsDismiss = () => {
    localStorage.setItem('pwa-mobile-instructions-seen', 'true');
    setShowMobileInstructions(false);
  };

  const handleDesktopInstall = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } catch (error) {
      console.error('Install prompt error:', error);
    }
    
    setShowDesktopPrompt(false);
  };

  // Don't render anything if already installed
  if (isStandalone) return null;

  return (
    <>
      {/* Desktop Install Button - Shows when install is available */}
      {deferredPrompt && (
        <Button
          onClick={() => setShowDesktopPrompt(true)}
          variant="outline"
          size="sm"
          className="fixed bottom-4 right-4 z-50 bg-white shadow-lg border-orange-300 hover:bg-orange-50 hover:border-orange-400 gap-2"
        >
          <Download className="h-4 w-4 text-orange-500" />
          <span className="hidden sm:inline">Install App</span>
        </Button>
      )}

      {/* Success Toast */}
      {installSuccess && (
        <div className="fixed bottom-4 right-4 z-50 bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-in slide-in-from-bottom-4">
          <Check className="h-5 w-5" />
          <span>App installed successfully!</span>
        </div>
      )}

      {/* Mobile Instructions Modal */}
      <Dialog open={showMobileInstructions} onOpenChange={setShowMobileInstructions}>
        <DialogContent className="sm:max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Smartphone className="h-6 w-6 text-orange-500" />
              Install Driver Shift Tracker
            </DialogTitle>
            <DialogDescription>
              Add this app to your home screen for quick access and a better experience.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {isIOS ? (
              // iOS Instructions
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-semibold">
                      1
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Tap the Share button</p>
                      <p className="text-sm text-gray-500 mt-1">
                        Look for the <Share className="inline h-4 w-4 mx-1" /> icon at the bottom of Safari
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-semibold">
                      2
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Scroll and tap "Add to Home Screen"</p>
                      <p className="text-sm text-gray-500 mt-1">
                        Look for the <Plus className="inline h-4 w-4 mx-1 border border-gray-400 rounded" /> icon in the menu
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-semibold">
                      3
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Tap "Add" to confirm</p>
                      <p className="text-sm text-gray-500 mt-1">
                        The app will appear on your home screen
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 text-sm text-gray-500 bg-blue-50 p-3 rounded-lg">
                  <svg className="h-5 w-5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <span>Make sure you're using Safari for the best experience</span>
                </div>
              </div>
            ) : isAndroid ? (
              // Android Instructions
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-semibold">
                      1
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Tap the menu button</p>
                      <p className="text-sm text-gray-500 mt-1">
                        Look for <MoreVertical className="inline h-4 w-4 mx-1" /> in the top right corner of Chrome
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-semibold">
                      2
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Tap "Add to Home screen"</p>
                      <p className="text-sm text-gray-500 mt-1">
                        Or tap "Install app" if you see that option
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-semibold">
                      3
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Tap "Add" or "Install"</p>
                      <p className="text-sm text-gray-500 mt-1">
                        The app will be added to your home screen
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 text-sm text-gray-500 bg-blue-50 p-3 rounded-lg">
                  <svg className="h-5 w-5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <span>Use Chrome for the best installation experience</span>
                </div>
              </div>
            ) : null}
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleMobileInstructionsDismiss}>
              Maybe Later
            </Button>
            <Button onClick={handleMobileInstructionsDismiss} className="bg-orange-500 hover:bg-orange-600">
              Got It!
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Desktop Install Confirmation Modal */}
      <Dialog open={showDesktopPrompt} onOpenChange={setShowDesktopPrompt}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Monitor className="h-6 w-6 text-orange-500" />
              Install Driver Shift Tracker
            </DialogTitle>
            <DialogDescription>
              Install this app on your computer for quick access from your desktop or taskbar.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <img 
                src="https://d64gsuwffb70l.cloudfront.net/68429d3677b256393c99965a_1765420434343_43832318.png" 
                alt="App Icon" 
                className="w-16 h-16 rounded-xl shadow-md"
              />
              <div>
                <h3 className="font-semibold text-gray-900">Driver Shift Tracker</h3>
                <p className="text-sm text-gray-500">Manage shifts and payments</p>
              </div>
            </div>
            
            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Check className="h-4 w-4 text-green-500" />
                <span>Works offline</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Check className="h-4 w-4 text-green-500" />
                <span>Quick launch from desktop</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Check className="h-4 w-4 text-green-500" />
                <span>No browser tabs needed</span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowDesktopPrompt(false)}>
              Not Now
            </Button>
            <Button onClick={handleDesktopInstall} className="bg-orange-500 hover:bg-orange-600 gap-2">
              <Download className="h-4 w-4" />
              Install
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PWAInstallPrompt;
